import get from 'get-value'
import React, { FC, MutableRefObject, ReactElement, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import Loader from '../../React/Common/Components/Loader'
import { getFeaturesConfig } from '../../React/Common/API/GenericConfigAPI'
import { FeaturesConfigDTO } from '../../NonReact/Types/Types'
import { JSONResponse } from '../../NonReact/common/Types/Types'
import { redirectToServlet, ServletCmds } from '../../NonReact/constants/ServletCommands'
import { ENABLE_NEW_CODE_LIST_PAGE } from '../Constants/FeatureFlags'
import { ReactState } from '../../React/Common/Types/Types'
import { buildLegacyAdminEntityListQueryFromUrl } from '../Utils/adminListLegacyRedirect'

type GateState = 'loading' | 'react' | 'legacy'

type Props = {
    children: ReactElement
}

/**
 * Loads {@link ENABLE_NEW_CODE_LIST_PAGE} from features config before rendering the routed
 * Codes list. When disabled, redirects to the legacy JSP admin page.
 *
 * @param props - Child tree to render when the feature is enabled.
 * @returns Loader, nothing (after servlet redirect), or children.
 */
export const CodeListFeatureGate: FC<Props> = ({ children }: Props): ReactElement => {
    const [gate, setGate]: ReactState<GateState> = useState<GateState>('loading')
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    useEffect((): void => {
        getFeaturesConfig()
            .then((response: JSONResponse<FeaturesConfigDTO>): void => {
                if (!isMounted.current) return

                const enabled: boolean = !!get(response ?? {}, ENABLE_NEW_CODE_LIST_PAGE)
                setGate(enabled ? 'react' : 'legacy')
            })
            .catch((): void => {
                if (isMounted.current) setGate('legacy')
            })
    }, [])

    if (gate === 'loading') {
        return (
            <div className='router-loader-container'>
                <Loader />
            </div>
        )
    }

    if (gate === 'legacy') {
        redirectToServlet(ServletCmds.ADMIN_CODES, buildLegacyAdminEntityListQueryFromUrl(window.location.search))

        return <></>
    }

    return children
}
